import React from 'react';
import Popup from '@atlaskit/popup';
import { Box } from '@atlaskit/primitives';
import { fg } from '@atlassian/jira-feature-gating';
import PaginatedAsyncSelectView from './async-select/index.tsx';
import formatOptionLabelDefault from './format-option-label/index.tsx';
import { FixedWidthPopupComponent } from './popup-container/index.tsx';
import type { PickerProps } from './types.tsx';

export const Picker = ({
	isOpen: isPickerOpen,
	value,
	onClose,
	onClearValues,
	onRestoreDefaults,
	id,
	invalidLabel,
	onChange,
	formatOptionLabel = formatOptionLabelDefault,
	onLoadOptions,
	enableFullList,
	operandDisplayValues,
	showOperandDisplayValuesInFullList,
	PopupComponent,
	onTrigger,
	placement = 'bottom-start',
	zIndex,
	CustomNoOptionsMessage,
	pickerComponents,
	fallbackPlacements,
	showOperatorSelect = false,
	onChangeOperator,
	allowedOperators,
	selectedOperator,
	isMulti,
	searchInputPlaceholder,
	shouldRenderToParent = false,
	experienceAnalyticsAttributes,
	isLoading = false,
	CustomFooter,
	isInBetweenColumnPickerEnabled = false,
	isInfiniteScrollEnabled = false,
}: PickerProps) => (
	<Box
		role="presentation"
		onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
			if (event.key === 'Escape') {
				// avoid closing a modal on closing a picker with esc key
				isPickerOpen && event.stopPropagation();
				onClose && onClose(event);
			}
		}}
	>
		<Popup
			key={id}
			zIndex={zIndex}
			placement={placement}
			isOpen={isPickerOpen}
			onClose={onClose}
			popupComponent={PopupComponent ?? FixedWidthPopupComponent}
			trigger={onTrigger}
			autoFocus={false}
			fallbackPlacements={fallbackPlacements}
			testId={`jql-builder-basic-picker-${id}`}
			shouldRenderToParent={shouldRenderToParent}
			/* Our default popup component supports resizing to fit the viewport. */
			shouldFitViewport={PopupComponent === undefined}
			content={({ setInitialFocusRef }) => (
				<PaginatedAsyncSelectView
					id={id}
					invalidLabel={invalidLabel}
					enableFullList={enableFullList}
					operandDisplayValues={operandDisplayValues}
					showOperandDisplayValuesInFullList={showOperandDisplayValuesInFullList}
					loadOptions={onLoadOptions}
					onChange={onChange}
					value={value}
					formatOptionLabel={formatOptionLabel}
					onClear={onClearValues}
					onRestoreDefaults={onRestoreDefaults}
					NoOptionsMessage={CustomNoOptionsMessage}
					components={pickerComponents}
					showOperatorSelect={showOperatorSelect}
					onChangeOperator={onChangeOperator}
					allowedOperators={allowedOperators}
					selectedOperator={selectedOperator}
					isMulti={isMulti}
					placeholder={searchInputPlaceholder}
					setInitialFocusRef={setInitialFocusRef}
					experienceAnalyticsAttributes={experienceAnalyticsAttributes}
					isLoading={isLoading}
					isInBetweenColumnPickerEnabled={isInBetweenColumnPickerEnabled}
					CustomFooter={fg('jira_inline_field_config_gate') ? CustomFooter : undefined}
					isInfiniteScrollEnabled={isInfiniteScrollEnabled}
				/>
			)}
		/>
	</Box>
);

Picker.whyDidYouRender = {
	customName: 'Picker',
};

export default Picker;
